import { UserAdvanceProgress } from "../../../../../types"
import { monthsBetween } from "../../../../../utility"

export type AdvanceRecoupment = {
  month: string
  amount: number
  recouped: boolean
  remaining: number
  // Month the advance is fully recouped. Actual month when recouped, projected month otherwise.
  // Empty when the advance can not be recouped within the projection horizon.
  completionMonth: string
  // Months from the last actual royalty month until this advance is recouped. 0 when already recouped.
  estimatedMonths: number | null
  // Months from the first advance until this advance - and every advance before it - is recouped.
  totalMonths: number | null
  // Sequential breakdown of the values above, e.g. [7, 1] -> "8 (7+1)"
  estimatedParts: number[]
  totalParts: number[]
}

export type AdvanceRecoupmentResult = {
  advanceList: AdvanceRecoupment[]
  advanceMap: { [month: string]: AdvanceRecoupment }
  recoupedMonthTracker: { [month: string]: boolean }
  // Recoupment of the whole advance stack, taken from the last advance
  completionMonth: string
  completionEstimatedMonths: number | null
  completionTotalMonths: number | null
  completionRecouped: boolean
}

const isCalendarMonth = (month: string) => /^\d{6}$/.test(month)

// Month keys are either calendar months (202603) or timeline keys (M0, M1, ... M10)
const monthOrder = (month: string) => {
  const value = parseInt(isCalendarMonth(month) ? month : month.replace(/[^0-9]/g, ""))
  return isNaN(value) ? 0 : value
}

const sortMonths = (months: string[]) => months.slice().sort((a, b) => monthOrder(a) - monthOrder(b))

const monthDistance = (start: string, end: string, timeline: string[]) => {
  if (!start || !end) {
    return 0
  }
  if (isCalendarMonth(start) && isCalendarMonth(end)) {
    return monthsBetween(start, end)
  }
  const startIndex = timeline.indexOf(start)
  const endIndex = timeline.indexOf(end)
  if (startIndex < 0 || endIndex < 0) {
    return 0
  }
  return endIndex - startIndex
}

/**
 * Recoupment of every advance of a single user, calculated as one sequential process.
 *
 * Royalties pay off the advances in the order they were given (FIFO), so an advance only
 * starts recouping once every earlier advance is fully recouped. That is why the estimate
 * of the second advance is the estimate of the first one plus its own recoup time, and why
 * its total is the month the whole stack - not only that single advance - is recouped.
 *
 * Example: 1st advance 7 / 12 months -> 2nd advance 8 (7+1) / 13 (12+1) months
 */
export const calculateAdvanceRecoupment = (progress: UserAdvanceProgress): AdvanceRecoupmentResult => {
  const royalties = progress?.royalties ?? {}
  const advances = progress?.advances ?? {}
  const projections = progress?.projections ?? {}

  const royaltyMonths = sortMonths(Object.keys(royalties))
  const projectionMonths = sortMonths(Object.keys(projections))
  const advanceMonths = sortMonths(Object.keys(advances))
  const timeline = [
    ...sortMonths(Array.from(new Set([...royaltyMonths, ...advanceMonths]))),
    ...projectionMonths
  ]

  const firstAdvanceMonth = advanceMonths[0] || ""
  const totalRoyalty = royaltyMonths.reduce((sum, month) => sum + (royalties[month] || 0), 0)

  const advanceList: AdvanceRecoupment[] = []
  const advanceMap: { [month: string]: AdvanceRecoupment } = {}
  const recoupedMonthTracker: { [month: string]: boolean } = {}

  let cumulativeAdvances = 0
  let previousEstimated: number | null = 0
  let previousTotal: number | null = null
  let previousEstimatedParts: number[] = []
  let previousTotalParts: number[] = []

  advanceMonths.forEach((advanceMonth) => {
    const amount = advances[advanceMonth] || 0
    const previousCumulativeAdvances = cumulativeAdvances
    cumulativeAdvances += amount

    // Actual royalties recoup the advances in order, so this advance is closed on the month
    // the cumulative royalty covers the cumulative advance amount
    let cumulativeRoyalty = 0
    let actualRecoupMonth = ""
    for (const month of royaltyMonths) {
      cumulativeRoyalty += royalties[month] || 0
      if (cumulativeRoyalty >= cumulativeAdvances) {
        actualRecoupMonth = month
        break
      }
    }

    let recoupment: AdvanceRecoupment

    if (actualRecoupMonth) {
      recoupment = {
        month: advanceMonth,
        amount,
        recouped: true,
        remaining: 0,
        completionMonth: actualRecoupMonth,
        estimatedMonths: 0,
        totalMonths: monthDistance(firstAdvanceMonth, actualRecoupMonth, timeline),
        estimatedParts: [],
        totalParts: []
      }
    } else {
      // Royalties first pay off the previous advances, the rest belongs to this advance
      const royaltiesAvailableForThisAdvance = Math.max(0, totalRoyalty - previousCumulativeAdvances)
      const remaining = Math.max(0, amount - royaltiesAvailableForThisAdvance)

      // Projections have to close the whole outstanding balance before this advance is recouped
      let remainingToRecoup = cumulativeAdvances - totalRoyalty
      let estimatedMonths = 0
      let completionMonth = ""

      for (const projectionMonth of projectionMonths) {
        remainingToRecoup -= projections[projectionMonth] || 0
        estimatedMonths++
        if (remainingToRecoup <= 0) {
          completionMonth = projectionMonth
          break
        }
      }

      recoupment = {
        month: advanceMonth,
        amount,
        recouped: false,
        remaining,
        completionMonth,
        estimatedMonths: completionMonth ? estimatedMonths : null,
        totalMonths: completionMonth ? monthDistance(firstAdvanceMonth, completionMonth, timeline) : null,
        estimatedParts: [],
        totalParts: []
      }
    }

    if (recoupment.estimatedMonths !== null) {
      recoupment.estimatedParts =
        previousEstimatedParts.length && previousEstimated !== null
          ? [...previousEstimatedParts, recoupment.estimatedMonths - previousEstimated]
          : [recoupment.estimatedMonths]
    }
    if (recoupment.totalMonths !== null) {
      recoupment.totalParts =
        previousTotalParts.length && previousTotal !== null
          ? [...previousTotalParts, recoupment.totalMonths - previousTotal]
          : [recoupment.totalMonths]
    }

    if (recoupment.completionMonth) {
      recoupedMonthTracker[recoupment.completionMonth] = true
    }

    previousEstimated = recoupment.estimatedMonths
    previousTotal = recoupment.totalMonths
    previousEstimatedParts = recoupment.estimatedParts
    previousTotalParts = recoupment.totalParts

    advanceList.push(recoupment)
    advanceMap[advanceMonth] = recoupment
  })

  const lastAdvance = advanceList[advanceList.length - 1]

  return {
    advanceList,
    advanceMap,
    recoupedMonthTracker,
    completionMonth: lastAdvance?.completionMonth || "",
    completionEstimatedMonths: lastAdvance ? lastAdvance.estimatedMonths : null,
    completionTotalMonths: lastAdvance ? lastAdvance.totalMonths : null,
    completionRecouped: !!lastAdvance?.recouped
  }
}
